import { useState, useMemo, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client/react';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { BulkActions, AdminActions } from '../components/admin/AdminActions';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CallTrackingService } from '../services/callTrackingService';
import { CallLog, CallLogFilters, CallOutcome } from '../types/callTracking';
import { GET_CURRENT_USER } from '../graphql/queries';
import { format } from 'date-fns';
import { canPerformAction } from '../utils/permissions';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

type SortField = 'phoneNumber' | 'contactName' | 'duration' | 'outcome' | 'createdAt';
type SortDirection = 'asc' | 'desc';
type ViewDensity = 'compact' | 'comfortable' | 'spacious';

export function CallLogs() {
  const apolloClient = useApolloClient();
  const callTrackingService = new CallTrackingService(apolloClient);
  
  // Data
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');
  const [staffFilter, setStaffFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [viewDensity, setViewDensity] = useState<ViewDensity>('comfortable');
  const [selectedCallLogs, setSelectedCallLogs] = useState<Set<string>>(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Admin functions
  const handleDeleteCallLog = async (callLogId: string) => {
    if (!canPerformAction(user?.role, 'documents', 'delete')) { // Using documents permission for now
      alert('You do not have permission to delete call logs.');
      return;
    }
    
    try {
      // TODO: Implement delete call log mutation
      console.log('Deleting call log:', callLogId);
      alert('Call log deleted successfully');
      loadCallLogs();
    } catch (error) {
      console.error('Error deleting call log:', error);
      alert('Error deleting call log');
    }
  };

  const handleBulkDelete = async () => {
    if (!canPerformAction(user?.role, 'documents', 'delete')) { // Using documents permission for now
      alert('You do not have permission to delete call logs.');
      return;
    }
    
    try {
      // TODO: Implement bulk delete mutation
      console.log('Bulk deleting call logs:', Array.from(selectedCallLogs));
      setSelectedCallLogs(new Set());
      alert(`${selectedCallLogs.size} call logs deleted successfully`);
      loadCallLogs();
    } catch (error) {
      console.error('Error bulk deleting call logs:', error);
      alert('Error deleting call logs');
    }
  };

  const handleBulkExport = () => {
    // Get all filtered call logs (not just current page)
    let filteredLogs = callLogs.filter((log: CallLog) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          log.phoneNumber.includes(query) ||
          log.contactName?.toLowerCase().includes(query) ||
          log.contactEmail?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      if (outcomeFilter !== 'all' && log.outcome !== outcomeFilter) return false;
      if (staffFilter && log.staff?.id !== staffFilter) return false;
      
      if (dateRange.start || dateRange.end) {
        const logDate = new Date(log.createdAt);
        if (dateRange.start && logDate < new Date(dateRange.start)) return false;
        if (dateRange.end && logDate > new Date(dateRange.end)) return false;
      }
      
      return true;
    });

    const selectedData = filteredLogs.filter(l => selectedCallLogs.has(l.id));
    const csvContent = [
      ['Phone Number', 'Contact Name', 'Contact Email', 'Duration', 'Outcome', 'Staff Member', 'Date'].join(','),
      ...selectedData.map(l => [
        l.phoneNumber,
        `"${l.contactName || ''}"`,
        l.contactEmail || '',
        l.duration ? `${Math.floor(l.duration / 60)}:${(l.duration % 60).toString().padStart(2, '0')}` : '0:00',
        l.outcome,
        `"${l.staff ? `${l.staff.firstName} ${l.staff.lastName}` : ''}"`,
        format(new Date(l.createdAt), 'yyyy-MM-dd HH:mm')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selected-call-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCallLogs(new Set(paginatedCallLogs.map((l: CallLog) => l.id)));
    } else {
      setSelectedCallLogs(new Set());
    }
  };

  const handleSelectCallLog = (callLogId: string, checked: boolean) => {
    const newSelected = new Set(selectedCallLogs);
    if (checked) {
      newSelected.add(callLogId);
    } else {
      newSelected.delete(callLogId);
    }
    setSelectedCallLogs(newSelected);
  };

  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const user = userData?.getCurrentUser;

  useEffect(() => {
    loadCallLogs();
  }, []);

  const loadCallLogs = async () => {
    try {
      setLoading(true);
      const filters: CallLogFilters = {};
      console.log('[CallLogs] Loading call logs with filters:', filters);
      const result = await callTrackingService.getCallLogs(filters);
      console.log('[CallLogs] Received result:', result);
      console.log('[CallLogs] Number of items:', result.items.length);
      setCallLogs(result.items);
    } catch (error) {
      console.error('Error loading call logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique staff members for filter
  const staffMembers = useMemo(() => {
    const staffMap = new Map();
    callLogs.forEach((log: CallLog) => {
      if (log.staff && !staffMap.has(log.staff.id)) {
        staffMap.set(log.staff.id, log.staff);
      }
    });
    return Array.from(staffMap.values()).sort((a, b) => 
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    );
  }, [callLogs]);

  // Filtering, sorting, and pagination
  const { paginatedCallLogs, totalPages, totalFiltered, stats } = useMemo(() => {
    let filtered = callLogs.filter((log: CallLog) => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          log.phoneNumber.includes(query) ||
          log.contactName?.toLowerCase().includes(query) ||
          log.contactEmail?.toLowerCase().includes(query) ||
          (log.client && `${log.client.firstName} ${log.client.lastName}`.toLowerCase().includes(query)) ||
          (log.staff && `${log.staff.firstName} ${log.staff.lastName}`.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      
      // Outcome filter
      if (outcomeFilter !== 'all' && log.outcome !== outcomeFilter) return false;
      
      // Staff filter
      if (staffFilter && log.staffId !== staffFilter) return false;
      
      // Date range filter
      if (dateRange.start && dateRange.end) {
        const logDate = new Date(log.createdAt);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        if (logDate < startDate || logDate > endDate) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: CallLog, b: CallLog) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'phoneNumber':
          aVal = a.phoneNumber;
          bVal = b.phoneNumber;
          break;
        case 'contactName':
          aVal = a.contactName?.toLowerCase() || '';
          bVal = b.contactName?.toLowerCase() || '';
          break;
        case 'duration':
          aVal = a.duration;
          bVal = b.duration;
          break;
        case 'outcome':
          aVal = a.outcome;
          bVal = b.outcome;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate stats
    const totalDuration = filtered.reduce((sum, log) => sum + log.duration, 0);
    const answeredCalls = filtered.filter(log => log.outcome === 'ANSWERED').length;
    const voicemailCalls = filtered.filter(log => log.outcome === 'VOICEMAIL').length;
    const missedCalls = filtered.filter(log => log.outcome === 'NO_ANSWER' || log.outcome === 'BUSY').length;

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedCallLogs = filtered.slice(startIndex, endIndex);

    return { 
      paginatedCallLogs, 
      totalPages, 
      totalFiltered,
      stats: {
        total: callLogs.length,
        answered: answeredCalls,
        voicemail: voicemailCalls,
        missed: missedCalls,
        avgDuration: totalFiltered > 0 ? Math.round(totalDuration / totalFiltered) : 0,
      }
    };
  }, [callLogs, searchQuery, outcomeFilter, staffFilter, dateRange, sortField, sortDirection, currentPage, pageSize]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setOutcomeFilter('all');
    setStaffFilter('');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleExport = () => {
    // Export ALL filtered call logs, not just current page
    const allFilteredLogs = callLogs.filter((log: CallLog) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          log.phoneNumber.includes(query) ||
          log.contactName?.toLowerCase().includes(query) ||
          log.contactEmail?.toLowerCase().includes(query) ||
          (log.client && `${log.client.firstName} ${log.client.lastName}`.toLowerCase().includes(query)) ||
          (log.staff && `${log.staff.firstName} ${log.staff.lastName}`.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      if (outcomeFilter !== 'all' && log.outcome !== outcomeFilter) return false;
      if (staffFilter && log.staffId !== staffFilter) return false;
      if (dateRange.start && dateRange.end) {
        const logDate = new Date(log.createdAt);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        if (logDate < startDate || logDate > endDate) return false;
      }
      return true;
    });

    const csvContent = [
      ['Date', 'Phone Number', 'Contact Name', 'Duration', 'Outcome', 'Staff Member', 'Client', 'Notes'].join(','),
      ...allFilteredLogs.map((log: CallLog) => [
        format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm'),
        log.phoneNumber,
        log.contactName || '',
        callTrackingService.formatDuration(log.duration),
        log.outcome,
        log.staff ? `"${log.staff.firstName} ${log.staff.lastName}"` : '',
        log.client ? `"${log.client.firstName} ${log.client.lastName}"` : '',
        log.notes ? `"${log.notes.replace(/"/g, '""')}"` : '',
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const getOutcomeBadgeVariant = (outcome: CallOutcome | null | undefined): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    if (!outcome) return 'default';
    const variants: Record<string, any> = {
      ANSWERED: 'success',
      VOICEMAIL: 'warning',
      NO_ANSWER: 'default',
      BUSY: 'default',
      UNKNOWN: 'secondary',
    };
    return variants[outcome] || 'default';
  };

  const densityClasses = {
    compact: 'py-2',
    comfortable: 'py-3',
    spacious: 'py-4',
  };

  if (loading && callLogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-full px-6">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-4">
                <h1 className="text-lg font-semibold text-aalaw-charcoal">Call Logs</h1>
                <Badge variant="secondary" className="text-xs">
                  {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'call' : 'calls'}
                  {totalFiltered !== callLogs.length && ` of ${callLogs.length.toLocaleString()} total`}
                </Badge>
                {totalFiltered > 0 && (
                  <span className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
            <div className="flex items-center gap-2">
              {/* View Density */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                {(['compact', 'comfortable', 'spacious'] as ViewDensity[]).map((density) => (
                  <button
                    key={density}
                    onClick={() => setViewDensity(density)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      viewDensity === density
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title={density.charAt(0).toUpperCase() + density.slice(1)}
                  >
                    {density === 'compact' && '━'}
                    {density === 'comfortable' && '≡'}
                    {density === 'spacious' && '☰'}
                  </button>
                ))}
              </div>
              
              {/* Page Size Selector */}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
              
              <Button variant="outline" size="sm" onClick={handleExport}>
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full px-6 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Total Calls</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.answered.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Answered</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.voicemail.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Voicemail</p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{callTrackingService.formatDuration(stats.avgDuration)}</p>
                  <p className="text-sm text-gray-600">Avg Duration</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Bar */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              {/* Search */}
              <div className="col-span-3">
                <Input
                  type="text"
                  placeholder="Search phone, name, email..."
                  value={searchQuery}
                  onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                  className="w-full h-9 text-sm"
                />
              </div>

              {/* Outcome */}
              <div className="col-span-2">
                <select
                  value={outcomeFilter}
                  onChange={(e) => handleFilterChange(setOutcomeFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="all">All Outcomes</option>
                  <option value="ANSWERED">Answered</option>
                  <option value="VOICEMAIL">Voicemail</option>
                  <option value="NO_ANSWER">No Answer</option>
                  <option value="BUSY">Busy</option>
                  <option value="UNKNOWN">Unknown</option>
                </select>
              </div>

              {/* Staff */}
              <div className="col-span-2">
                <select
                  value={staffFilter}
                  onChange={(e) => handleFilterChange(setStaffFilter)(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">All Staff</option>
                  {staffMembers.map((staff: any) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.firstName} {staff.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="col-span-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => handleFilterChange((val: string) => setDateRange(prev => ({ ...prev, start: val })))(e.target.value)}
                  className="w-full h-9 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  placeholder="Start date"
                />
              </div>
              <div className="col-span-2">
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => handleFilterChange((val: string) => setDateRange(prev => ({ ...prev, end: val })))(e.target.value)}
                  className="w-full h-9 px-3 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  placeholder="End date"
                />
              </div>

              {/* Actions */}
              <div className="col-span-1 flex items-center justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-aalaw-red"
                  title="Clear all filters"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        <BulkActions
          userRole={user?.role}
          resource="documents" // Using documents permission for now
          selectedCount={selectedCallLogs.size}
          onBulkDelete={handleBulkDelete}
          onBulkExport={handleBulkExport}
          className="mb-4"
        />

        {/* Call Logs Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedCallLogs.size === paginatedCallLogs.length && paginatedCallLogs.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                    />
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('phoneNumber')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Phone Number
                      <SortIcon field="phoneNumber" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('contactName')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Contact
                      <SortIcon field="contactName" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Client
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Staff
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('duration')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Duration
                      <SortIcon field="duration" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('outcome')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Outcome
                      <SortIcon field="outcome" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                    >
                      Date
                      <SortIcon field="createdAt" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedCallLogs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <p className="text-gray-500">No call logs found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {searchQuery || outcomeFilter !== 'all' || staffFilter || dateRange.start || dateRange.end
                          ? 'Try adjusting your filters'
                          : 'Call logs will appear here once created'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedCallLogs.map((log: CallLog) => (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedCallLogs.has(log.id)}
                          onChange={(e) => handleSelectCallLog(log.id, e.target.checked)}
                          className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                        />
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm font-mono text-gray-900">
                          {callTrackingService.formatPhoneNumber(log.phoneNumber)}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{log.contactName}</div>
                          {log.contactEmail && (
                            <div className="text-xs text-gray-500">{log.contactEmail}</div>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        {log.client ? (
                          <span className="text-sm text-gray-700">
                            {log.client.firstName} {log.client.lastName}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        {log.staff ? (
                          <span className="text-sm text-gray-700">
                            {log.staff.firstName} {log.staff.lastName}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <span className="text-sm text-gray-700">
                          {callTrackingService.formatDuration(log.duration)}
                        </span>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <Badge variant={getOutcomeBadgeVariant(log.outcome)} className="text-xs">
                          {log.outcome ? log.outcome.replace('_', ' ') : 'N/A'}
                        </Badge>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        <div className="text-sm text-gray-700">
                          {format(new Date(log.createdAt), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(log.createdAt), 'h:mm a')}
                        </div>
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`}>
                        {log.notes ? (
                          <span className="text-sm text-gray-600 line-clamp-2">
                            {log.notes}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`px-4 ${densityClasses[viewDensity]}`} onClick={(e) => e.stopPropagation()}>
                        <AdminActions
                          userRole={user?.role}
                          resource="documents" // Using documents permission for now
                          onDelete={() => handleDeleteCallLog(log.id)}
                          itemName={`Call from ${log.phoneNumber}`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} call logs
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((page, index) => (
                      typeof page === 'string' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                      ) : (
                        <button
                          key={`page-${page}`}
                          onClick={() => goToPage(page)}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            currentPage === page
                              ? 'bg-aalaw-red text-white font-semibold'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </main>
      </div>
    </div>
  );
}
